import { AlertTriangle, CircleSlash, CheckCircle2, ExternalLink, FileText, FolderTree, Stethoscope } from "lucide-react";
import type { CoverageResult, CoverageMatch, CoverageMatchBasis, PublishedKind } from "../../lib/api.js";

/**
 * What the match is actually built on, in the operator's words. "title match"
 * vs "topic overlap" hid the distinction that mattered: an operator-declared
 * keyword and a stray shared tag both used to read as one or the other.
 */
function basisLabel(basis: CoverageMatchBasis): string {
  if (basis === "operator-keyword") return "its operator keyword is this phrase";
  if (basis === "title") return "title match";
  if (basis === "title-tokens") return "same words in the title, different order";
  return "topic overlap";
}

/** Human label + icon for each Sanity content kind. */
function kindMeta(kind: PublishedKind): { label: string; Icon: typeof FileText } {
  if (kind === "article") return { label: "Blog article", Icon: FileText };
  if (kind === "treatment_category") return { label: "Treatment hub", Icon: FolderTree };
  return { label: "Treatment page", Icon: Stethoscope };
}

/**
 * Coverage badge — "do we already have a live page for this keyword?"
 *
 *   covered  → amber/caution: a live page already targets this. Likely a
 *              duplicate → refresh the existing page, don't produce new.
 *   suspect  → amber/quiet: scores in the "partial" band, but every word of the
 *              keyword is already in a live TITLE. This is the state that read
 *              "Partial · 79% · Safe to produce" over an article we had already
 *              published, so it no longer borrows the neutral styling.
 *   partial  → slate/neutral: related live content exists but no dedicated page.
 *   gap      → emerald/positive: no coverage → genuine gap, good to produce.
 *
 * The hover title carries the best-matching page + the operator recommendation.
 */
export function CoverageBadge({ coverage, compact }: { coverage: CoverageResult | undefined; compact?: boolean }) {
  if (!coverage) return null;
  const { verdict, score, best, recommendation } = coverage;

  const suspect = verdict === "partial" && best?.basis === "title-tokens";
  const cfg =
    verdict === "covered"
      ? { color: "bg-amber-100 text-amber-800", Icon: AlertTriangle, label: "Covered" }
      : suspect
      ? { color: "bg-amber-50 text-amber-700", Icon: AlertTriangle, label: "Possible duplicate" }
      : verdict === "partial"
      ? { color: "bg-slate-100 text-slate-600", Icon: CircleSlash, label: "Partial" }
      : { color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, label: "Gap" };

  const tip =
    (best ? `${best.title} (${best.kind.replace("treatment_", "")}, ${(score * 100).toFixed(0)}%)\n` : "") +
    recommendation;

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${cfg.color}`}
      title={tip}
    >
      <cfg.Icon className="h-3 w-3" />
      {compact ? cfg.label : `${cfg.label}${verdict !== "gap" ? ` · ${(score * 100).toFixed(0)}%` : ""}`}
    </span>
  );
}

/** One matched page → a clickable row linking to the live asset. */
function MatchRow({ match, primary }: { match: CoverageMatch; primary?: boolean }) {
  const { label, Icon } = kindMeta(match.kind);
  return (
    <a
      href={match.url}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 hover:bg-canvas ${primary ? "border-line bg-white" : "border-line/60"}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-ink" dir="auto" title={match.title}>{match.title}</span>
        <span className="text-[10px] text-muted">
          {label} · {(match.score * 100).toFixed(0)}% · {basisLabel(match.basis)}
        </span>
      </span>
      <ExternalLink className="h-3 w-3 shrink-0 text-muted" />
    </a>
  );
}

/**
 * Full coverage card for the keyword detail panel — names the matched page,
 * its kind (blog article vs treatment page), links straight to the live asset,
 * and shows the operator recommendation + runner-up matches.
 */
export function CoverageCard({ coverage, loading }: { coverage: CoverageResult | undefined; loading?: boolean }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-line bg-canvas-deep/30 p-4 text-xs text-muted">
        Checking existing coverage…
      </div>
    );
  }
  if (!coverage) return null;
  const { verdict, best, alternatives, recommendation } = coverage;

  const tone =
    coverage.suspectedDuplicate ? "border-amber-200 bg-amber-50"
    : verdict === "partial" ? "border-slate-200 bg-slate-50"
    : "border-emerald-200 bg-emerald-50";

  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-2xs font-semibold uppercase tracking-wider text-muted-dark">
          Existing coverage
        </div>
        <CoverageBadge coverage={coverage} />
      </div>
      <p className="mt-1.5 text-sm text-ink">{recommendation}</p>

      {best ? (
        <div className="mt-2.5 space-y-1.5">
          <MatchRow match={best} primary />
          {alternatives.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-2xs font-medium text-muted hover:text-ink">
                {alternatives.length} other near match{alternatives.length === 1 ? "" : "es"}
              </summary>
              <div className="mt-1.5 space-y-1">
                {alternatives.map((m) => <MatchRow key={m.sanityId} match={m} />)}
              </div>
            </details>
          )}
        </div>
      ) : (
        <p className="mt-1 text-2xs text-muted">No live page matched — nothing to link or refresh.</p>
      )}
    </div>
  );
}
